import { vars } from '@styles/vanilla-extract/theme.css';

import {
	RatioSingleBarChartItem,
	RatioSingleBarChartProps,
} from '@components/report/chart/ratio-single-bar-chart/RatioSingleBarChart';

import { TriangleChartProps } from '@components/report/chart/triangle-chart/TriangleChart';
import {
	NineGridPicturesCardProps,
	NineGridPicturesItem,
} from '@components/report/nine-grid-pictures-card/NineGridPicturesCard';

import { IGContentSummary, IGReportRes } from '@apis/orval/model';

import round, { roundFloat } from '@utils/round';

import { ContentIgProps } from './ContentIg';

export class ContentCommonIgAdapter {
	value: IGReportRes;

	constructor(value: IGReportRes) {
		this.value = value;
	}

	get bannerHashtags() {
		const mainHashTags = this.value?.content?.main_hashtags ?? [];
		return (mainHashTags.map((i) => i) as string[]).slice(0, 5);
	}

	get contentType(): RatioSingleBarChartProps {
		const feedPercentage = (this.value?.content?.summary?.feed_rate ?? 0) * 100;
		const videoPercentage = (this.value?.content?.summary?.video_rate ?? 0) * 100;

		const items: RatioSingleBarChartItem[] = [
			{
				label: '피드',
				value: `${roundFloat(feedPercentage, 'down', 0)}%`,
				valuePercentage: feedPercentage,
				color: vars.global.colors.primary[40],
			},
			{
				label: '동영상(릴스+비디오)',
				value: `${roundFloat(videoPercentage, 'down', 0)}%`,
				valuePercentage: videoPercentage,
				color: vars.global.colors.primary[60],
			},
		];

		return {
			desc: (
				<>
					전체 콘텐츠 중에서{' '}
					<span>
						피드는 {roundFloat(feedPercentage, 'down', 0)}%, 동영상은{' '}
						{roundFloat(videoPercentage, 'down', 0)}%
					</span>
					를 차지하고 있습니다.
				</>
			),
			items,
		};
	}

	get nineGridPictures(): NineGridPicturesCardProps {
		const recentNineContents: NineGridPicturesItem[] = (
			this.value?.content?.recent_contents ?? []
		)
			.slice(0, 9)
			.map((c) => c)
			.map(
				(content) =>
					({
						src: content.content_img_link,
						link: content.content_link,
					} as NineGridPicturesItem),
			);

		return {
			images: recentNineContents,
		};
	}

	adapt(): Pick<ContentIgProps, 'bannerHashtags' | 'contentType' | 'nineGridPictures'> {
		return {
			bannerHashtags: this.bannerHashtags,
			contentType: this.contentType,
			nineGridPictures: this.nineGridPictures,
		};
	}
}

type KeyOfFullIGContentSummary = keyof Exclude<IGContentSummary, null>;
type KeyOfIGContentSummary = Exclude<
	KeyOfFullIGContentSummary,
	| 'is_avg_feed_comment_private'
	| 'is_avg_feed_like_private'
	| 'is_avg_video_comment_private'
	| 'is_avg_video_like_private'
>;

export class ContentTriangleChartAdapter {
	value: IGReportRes;
	rateField: KeyOfIGContentSummary;
	rateRankField: KeyOfIGContentSummary;
	isPrivate?: boolean;
	privateText?: string;
	rateUnit?: string; // i18next 키 대신 직접적인 텍스트를 받도록 사용

	constructor(
		value: IGReportRes,
		rateField: KeyOfIGContentSummary,
		rateRankField: KeyOfIGContentSummary,
		rateUnit?: string,
		isPrivate?: boolean,
		privateText?: string,
	) {
		this.value = value;
		this.rateField = rateField;
		this.rateRankField = rateRankField;
		this.rateUnit = rateUnit ?? '개';
		this.isPrivate = isPrivate;
		this.privateText = privateText;
	}

	get rate() {
		return this.value?.content?.summary?.[this.rateField] ?? 0;
	}

	get rateRank() {
		if (this.isPrivate) return 100;

		return this.value?.content?.summary?.[this.rateRankField] ?? 0;
	}

	get ratePrimaryText() {
		if (this.isPrivate) return this.privateText;
		else return `${round(this.rate, 'down')} ${this.rateUnit}`; // 단위 텍스트를 직접 사용
	}

	get hasNoContent() {
		const feedCount = this.value?.content?.summary?.feed_count;
		const videoCount = this.value?.content?.summary?.video_count;

		const contentCounts = {
			feed: feedCount,
			video: videoCount,
		} as const;

		let targetContent = '';
		if (this.rateField.includes('feed')) {
			targetContent = 'feed';
		} else if (this.rateField.includes('video')) {
			targetContent = 'video';
		} else {
			return false;
		}

		const targetCount = contentCounts[targetContent as 'feed' | 'video'];

		if (!targetCount?.toString()) return false;

		return targetCount === 0;
	}

	adapt(keyword: string): TriangleChartProps {
		if (this.hasNoContent) {
			return {
				desc: `해당 계정에 업로드된 콘텐츠가 없어\n분석을 진행할 수 없습니다.`,
				valuePercentage: undefined,
			};
		}

		return {
			desc: (
				<>
					{keyword}는 <span>{this.ratePrimaryText}</span>
					로<br />
					유사 그룹 중{' '}
					<span>
						상위 {this.rateRank ? `${roundFloat(this.rateRank, 'down', 2)}%` : '-'}
					</span>
					에 해당됩니다.
				</>
			),
			valuePercentage: this.rateRank ? this.rateRank : undefined,
		};
	}
}

export class ContentIgAdapter {
	value: IGReportRes;
	constructor(value: IGReportRes) {
		this.value = value;
	}

	adapt(): ContentIgProps {
		return {
			...new ContentCommonIgAdapter(this.value).adapt(),
			averageFeedLike: new ContentTriangleChartAdapter(
				this.value,
				'avg_feed_like',
				'avg_feed_like_rank_in_similar_group',
				undefined,
				Boolean(this.value?.content?.summary?.is_avg_feed_like_private),
				'비공개',
			).adapt('피드 좋아요 수'),
			averageFeedComment: new ContentTriangleChartAdapter(
				this.value,
				'avg_feed_comment',
				'avg_feed_comment_rank_in_similar_group',
				undefined,
				Boolean(this.value?.content?.summary?.is_avg_feed_comment_private),
				'비활성화',
			).adapt('피드 댓글 수'),
			averageVideoView: new ContentTriangleChartAdapter(
				this.value,
				'avg_video_view',
				'avg_video_view_rank_in_similar_group',
				'회',
			).adapt('동영상 조회 수'),
			averageVideoLike: new ContentTriangleChartAdapter(
				this.value,
				'avg_video_like',
				'avg_video_like_rank_in_similar_group',
				undefined,
				Boolean(this.value?.content?.summary?.is_avg_video_like_private),
				'비공개',
			).adapt('동영상 좋아요 수'),
			averageVideoComment: new ContentTriangleChartAdapter(
				this.value,
				'avg_video_comment',
				'avg_video_comment_rank_in_similar_group',
				undefined,
				Boolean(this.value?.content?.summary?.is_avg_video_comment_private),
				'비활성화',
			).adapt('동영상 댓글 수'),
		};
	}
}
